import { Action } from "redux";

import { APIFailureAction, APIRequestAction, APISuccessAction } from "@/mk2/helpers/action";
import { NormalizedResponse } from "@/mk2/helpers/api";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";

///////////////////////////////////////////////////////////////////
// Progress broadcast

export interface ForumTopicFetchAction extends Action {
    readonly slug: string; // Owner of mentions
    readonly after: number; // Timestamp of the last mention in the list - for infinity scroll
    readonly hashtags: string[];
    readonly query: string;
}

/////////////////////////////////////////////////////////////////////
// Topic load
export const TOPIC_FETCH_TRIGGER = "TOPIC_FETCH_TRIGGER";
export interface TopicFetchTriggerAction extends ForumTopicFetchAction {
    readonly type: typeof TOPIC_FETCH_TRIGGER;
    readonly location: HistoryLocationWithState;
}
export const TOPIC_FETCH_REQUEST = "TOPIC_FETCH_REQUEST";
export interface TopicFetchRequestAction extends ForumTopicFetchAction, APIRequestAction {
    readonly type: typeof TOPIC_FETCH_REQUEST;
}
export interface TopicFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        topic: number;
        posts: number[];
        myAnonymousPosts: number[];
        brandDaysCampaign: number;
        brandDaysCampaignGreetingText: string;
        counselling: number;
        isSubscribed: boolean;
        isFavorite: boolean;
        counsellingPostsCount: number | null;
        subscribers: number;
        activeBrandDays: { posts: number[]; campaign: number };
    };
}
export const TOPIC_FETCH_SUCCESS = "TOPIC_FETCH_SUCCESS";
export interface TopicFetchSuccessAction
    extends ForumTopicFetchAction, APISuccessAction<TopicFetchNormalizedResponse> {
    readonly type: typeof TOPIC_FETCH_SUCCESS;
    readonly hasNext: number;
    readonly hasPrevious: number;
    readonly notificationsOn: number[];
}
export const TOPIC_FETCH_FAILURE = "TOPIC_FETCH_FAILURE";
export interface TopicFetchFailureAction extends ForumTopicFetchAction, APIFailureAction {
    readonly type: typeof TOPIC_FETCH_FAILURE;
}

export const topicFetchTrigger = (
    slug: string,
    hashtags: string[],
    query: string,
    after: number,
    location: HistoryLocationWithState,
): TopicFetchTriggerAction => ({
    type: TOPIC_FETCH_TRIGGER,
    slug,
    hashtags,
    query,
    after,
    location,
});
export const topicFetchApi = {
    request: (
        slug: string,
        hashtags: string[],
        query: string,
        after: number,
    ): TopicFetchRequestAction => ({
        type: TOPIC_FETCH_REQUEST,
        slug,
        hashtags,
        query,
        after,
    }),
    success: (
        slug: string,
        hashtags: string[],
        query: string,
        after: number,
        hasNext: number,
        hasPrevious: number,
        response: TopicFetchNormalizedResponse,
        notificationsOn: number[],
    ): TopicFetchSuccessAction => ({
        type: TOPIC_FETCH_SUCCESS,
        slug,
        hashtags,
        query,
        after,
        hasNext,
        hasPrevious,
        response,
        notificationsOn,
    }),
    failure: (
        slug: string,
        hashtags: string[],
        query: string,
        after: number,
        error: string,
    ): TopicFetchFailureAction => ({
        type: TOPIC_FETCH_FAILURE,
        slug,
        hashtags,
        query,
        after,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Topic subscribe
export const TOPIC_SUBSCRIBE_TRIGGER = "TOPIC_SUBSCRIBE_TRIGGER";
export interface TopicSubscribeTriggerAction {
    readonly type: typeof TOPIC_SUBSCRIBE_TRIGGER;
    readonly slug: string;
    readonly username: string;
}
export const TOPIC_SUBSCRIBE_REQUEST = "TOPIC_SUBSCRIBE_REQUEST";
export interface TopicSubscribeRequestAction extends APIRequestAction {
    readonly type: typeof TOPIC_SUBSCRIBE_REQUEST;
    readonly slug: string;
}
export const TOPIC_SUBSCRIBE_SUCCESS = "TOPIC_SUBSCRIBE_SUCCESS";
export interface TopicSubscribeNormalizedResponse extends NormalizedResponse {
    readonly result: { topic: number; isSubscribed: boolean };
}
export interface TopicSubscribeSuccessAction extends APISuccessAction<TopicSubscribeNormalizedResponse> {
    readonly type: typeof TOPIC_SUBSCRIBE_SUCCESS;
    readonly slug: string;
    isSubscribed: boolean;
}

export const TOPIC_SUBSCRIBE_FAILURE = "TOPIC_SUBSCRIBE_FAILURE";
export interface TopicSubscribeFailureAction extends APIFailureAction {
    readonly type: typeof TOPIC_SUBSCRIBE_FAILURE;
    readonly slug: string;
}

export const topicSubscribeTrigger = (
    slug: string,
    username: string,
): TopicSubscribeTriggerAction => ({
    type: TOPIC_SUBSCRIBE_TRIGGER,
    slug,
    username,
});
export const topicSubscribeApi = {
    request: (slug: string): TopicSubscribeRequestAction => ({
        type: TOPIC_SUBSCRIBE_REQUEST,
        slug,
    }),
    success: (slug: string, isSubscribed: boolean): TopicSubscribeSuccessAction => ({
        type: TOPIC_SUBSCRIBE_SUCCESS,
        slug,
        isSubscribed,
        response: null,
    }),
    failure: (slug: string, error: string): TopicSubscribeFailureAction => ({
        type: TOPIC_SUBSCRIBE_FAILURE,
        slug,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Topic Unsubscribe
export const TOPIC_UNSUBSCRIBE_TRIGGER = "TOPIC_UNSUBSCRIBE_TRIGGER";
export interface TopicUnsubscribeTriggerAction {
    readonly type: typeof TOPIC_UNSUBSCRIBE_TRIGGER;
    readonly slug: string;
    readonly username: string;
}
export const TOPIC_UNSUBSCRIBE_REQUEST = "TOPIC_UNSUBSCRIBE_REQUEST";
export interface TopicUnsubscribeRequestAction extends APIRequestAction {
    readonly type: typeof TOPIC_UNSUBSCRIBE_REQUEST;
    readonly slug: string;
}
export const TOPIC_UNSUBSCRIBE_SUCCESS = "TOPIC_UNSUBSCRIBE_SUCCESS";
export interface TopicUnsubscribeSuccessAction extends APISuccessAction {
    readonly type: typeof TOPIC_UNSUBSCRIBE_SUCCESS;
    readonly slug: string;
    isSubscribed: boolean;
}
export const TOPIC_UNSUBSCRIBE_FAILURE = "TOPIC_UNSUBSCRIBE_FAILURE";
export interface TopicUnsubscribeFailureAction extends APIFailureAction {
    readonly type: typeof TOPIC_UNSUBSCRIBE_FAILURE;
    readonly slug: string;
}

export const topicUnsubscribeTrigger = (
    slug: string,
    username: string,
): TopicUnsubscribeTriggerAction => ({
    type: TOPIC_UNSUBSCRIBE_TRIGGER,
    slug,
    username,
});
export const topicUnsubscribeApi = {
    request: (slug: string): TopicUnsubscribeRequestAction => ({
        type: TOPIC_UNSUBSCRIBE_REQUEST,
        slug,
    }),
    success: (slug: string, isSubscribed: boolean): TopicUnsubscribeSuccessAction => ({
        type: TOPIC_UNSUBSCRIBE_SUCCESS,
        slug,
        isSubscribed,
        response: null,
    }),
    failure: (slug: string, error: string): TopicUnsubscribeFailureAction => ({
        type: TOPIC_UNSUBSCRIBE_FAILURE,
        slug,
        error,
    }),
};

///////////////////////////////////////////////////////////////////
// Topic - add or remove a topic to/from list of favorite groups and topics
interface TopicSetAsFavoriteAction {
    readonly slug: string;
    readonly userId: number;
    readonly setAsFavorite: boolean;
}
export const TOPIC_SET_AS_FAVORITE_TRIGGER = "TOPIC_SET_AS_FAVORITE_TRIGGER";
export interface TopicSetAsFavoriteTriggerAction {
    readonly type: typeof TOPIC_SET_AS_FAVORITE_TRIGGER;
    readonly topicId: number;
    readonly userId: number;
    readonly setAsFavorite: boolean;
}
export const TOPIC_SET_AS_FAVORITE_REQUEST = "TOPIC_SET_AS_FAVORITE_REQUEST";
export interface TopicSetAsFavoriteRequestAction
    extends TopicSetAsFavoriteAction, APIRequestAction {
    readonly type: typeof TOPIC_SET_AS_FAVORITE_REQUEST;
}
export const TOPIC_SET_AS_FAVORITE_SUCCESS = "TOPIC_SET_AS_FAVORITE_SUCCESS";
export interface TopicSetAsFavoriteSuccessAction extends TopicSetAsFavoriteAction {
    readonly type: typeof TOPIC_SET_AS_FAVORITE_SUCCESS;
}
export const TOPIC_SET_AS_FAVORITE_FAILURE = "TOPIC_SET_AS_FAVORITE_FAILURE";
export interface TopicSetAsFavoriteFailureAction
    extends TopicSetAsFavoriteAction, APIFailureAction {
    readonly type: typeof TOPIC_SET_AS_FAVORITE_FAILURE;
}

export const topicSetAsFavoriteTrigger = (
    topicId: number,
    userId: number,
    setAsFavorite: boolean,
): TopicSetAsFavoriteTriggerAction => ({
    type: TOPIC_SET_AS_FAVORITE_TRIGGER,
    topicId,
    userId,
    setAsFavorite,
});
export const topicSetAsFavoriteApi = {
    request: (
        slug: string,
        userId: number,
        setAsFavorite: boolean,
    ): TopicSetAsFavoriteRequestAction => ({
        type: TOPIC_SET_AS_FAVORITE_REQUEST,
        slug,
        userId,
        setAsFavorite,
    }),
    success: (
        slug: string,
        userId: number,
        setAsFavorite: boolean,
    ): TopicSetAsFavoriteSuccessAction => ({
        type: TOPIC_SET_AS_FAVORITE_SUCCESS,
        slug,
        userId,
        setAsFavorite,
    }),
    failure: (
        slug: string,
        userId: number,
        setAsFavorite: boolean,
        error: string,
    ): TopicSetAsFavoriteFailureAction => ({
        type: TOPIC_SET_AS_FAVORITE_FAILURE,
        slug,
        userId,
        setAsFavorite,
        error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Mark topic as read

export const TOPIC_READ = "TOPIC_READ";

export interface TopicReadAction extends Action {
    readonly type: typeof TOPIC_READ;
    readonly topicId: number;
}

export const topicRead = (topicId: number): TopicReadAction => ({
    type: TOPIC_READ,
    topicId,
});

/////////////////////////////////////////////////////////////////////
// Topic Delete
export interface ForumDeleteTopicApiAction extends Action {
    readonly slug: string;
}

export const FORUM_DELETE_TOPIC_API_REQUEST = "FORUM_DELETE_TOPIC_API_REQUEST";

export interface ForumDeleteTopicApiRequestAction
    extends ForumDeleteTopicApiAction, APIRequestAction {
    readonly type: typeof FORUM_DELETE_TOPIC_API_REQUEST;
}

export const FORUM_DELETE_TOPIC_API_SUCCESS = "FORUM_DELETE_TOPIC_API_SUCCESS";

export interface TopicDeleteNormalizedResponse extends NormalizedResponse {
    readonly result: { topic: number; isSubscribed: boolean };
}

export interface ForumDeleteTopicApiSuccessAction
    extends ForumDeleteTopicApiAction, APISuccessAction<TopicDeleteNormalizedResponse> {
    readonly type: typeof FORUM_DELETE_TOPIC_API_SUCCESS;
}

export const FORUM_DELETE_TOPIC_API_FAILURE = "FORUM_DELETE_TOPIC_API_FAILURE";

export interface ForumDeleteTopicApiFailureAction
    extends ForumDeleteTopicApiAction, APIFailureAction {
    readonly type: typeof FORUM_DELETE_TOPIC_API_FAILURE;
}

export const forumDeleteTopicApi = {
    request: (slug: string): ForumDeleteTopicApiRequestAction => ({
        type: FORUM_DELETE_TOPIC_API_REQUEST,
        slug,
    }),
    success: (
        slug: string,
        response: TopicDeleteNormalizedResponse,
    ): ForumDeleteTopicApiSuccessAction => ({
        type: FORUM_DELETE_TOPIC_API_SUCCESS,
        slug,
        response,
    }),
    failure: (slug: string, error: string): ForumDeleteTopicApiFailureAction => ({
        type: FORUM_DELETE_TOPIC_API_FAILURE,
        slug,
        error,
    }),
};

export const FORUM_DELETE_TOPIC = "FORUM_DELETE_TOPIC";

export interface ForumDeleteTopicAction extends ForumDeleteTopicApiAction {
    readonly type: typeof FORUM_DELETE_TOPIC;
}

export const forumDeleteTopic = (slug: string): ForumDeleteTopicAction => ({
    type: FORUM_DELETE_TOPIC,
    slug,
});
